/**
 * Chain Execution Service
 *
 * Handles the execution of LLM prompt chains:
 * Position 1 (Global) → Position 2 (Regional) → Position 3 (Local)
 *
 * Each position can modify the output of the previous position.
 * The final output is from the last position in the chain that exists.
 *
 * NOTE: Production LLM execution uses Firebase Functions only (queryManagerOrchestrator).
 * llmProviderService.callLLMWithQuery() sends queryId + userInput to the backend; the client
 * does not resolve query config or call llmCallCallable for that path. This service is kept
 * for chain preview, UI (e.g. resolveChain for display), and any non-execution use.
 *
 * INPUT MODES:
 * - A: Only the previous position's output
 * - B: Previous output + original user input
 * - C: Full chain context (original input + all previous outputs)
 */

import { queryRegistryService } from './queryRegistryService';
import { vectorSearchService } from './vectorSearchService';
import { validatePrompt, addMedicalDisclaimer } from './promptValidationService';
import { llmProviderService } from './llmProviderService';
import { QUERY_CATEGORIES, CHAIN_POSITIONS } from '../../contexts/LLMManagerContext';
import { DEFAULT_MODEL } from './llmConstants';

// Input mode constants
export const INPUT_MODES = {
  PREVIOUS_ONLY: 'A',      // Only the previous position's output
  PREVIOUS_PLUS_ORIGINAL: 'B',  // Previous output + original user input
  FULL_CONTEXT: 'C',       // Original input + all previous outputs
};

/**
 * Build the input for a chain position based on its input mode
 * @param {string} inputMode - 'A', 'B', or 'C'
 * @param {Object} context - Chain execution context
 * @returns {Object} The input to pass to this position's LLM call
 */
const buildPositionInput = (inputMode, context) => {
  const { originalInput, previousOutput, chainHistory } = context;
  
  switch (inputMode) {
    case INPUT_MODES.PREVIOUS_ONLY:
      // Mode A: Only the previous position's output
      return {
        input: previousOutput,
        inputType: 'previous_output',
      };
    
    case INPUT_MODES.PREVIOUS_PLUS_ORIGINAL:
      // Mode B: Previous output + original user input
      return {
        originalInput,
        previousOutput,
        inputType: 'combined',
      };
    
    case INPUT_MODES.FULL_CONTEXT:
      // Mode C: Full chain context
      return {
        originalInput,
        chainHistory, // Array of { position, output } objects
        previousOutput,
        inputType: 'full_context',
      };
    
    default:
      // Default to Mode A
      return {
        input: previousOutput,
        inputType: 'previous_output',
      };
  }
};

/**
 * Format the prompt with the given input context
 * @param {string} promptTemplate - The prompt template
 * @param {Object} input - The input data
 * @param {string} inputMode - The input mode ('A', 'B', 'C')
 * @returns {string} The formatted prompt
 */
const formatPromptWithInput = (promptTemplate, input, inputMode) => {
  // The prompt template can include placeholders:
  // {{originalInput}} - The original user input
  // {{previousOutput}} - The previous position's output
  // {{chainHistory}} - JSON of all previous outputs
  
  let formattedPrompt = promptTemplate;
  
  // For Position 2 and 3, we need to inject the context
  if (input.inputType === 'previous_output') {
    // Mode A: Insert previous output
    formattedPrompt = `${promptTemplate}

---
PREVIOUS ANALYSIS TO REFINE:
${typeof input.input === 'string' ? input.input : JSON.stringify(input.input, null, 2)}
---

Based on the above analysis, provide your refined output:`;
  } else if (input.inputType === 'combined') {
    // Mode B: Insert original input and previous output
    formattedPrompt = `${promptTemplate}

---
ORIGINAL USER INPUT:
${typeof input.originalInput === 'string' ? input.originalInput : JSON.stringify(input.originalInput, null, 2)}

PREVIOUS ANALYSIS:
${typeof input.previousOutput === 'string' ? input.previousOutput : JSON.stringify(input.previousOutput, null, 2)}
---

Based on the original input and previous analysis, provide your refined output:`;
  } else if (input.inputType === 'full_context') {
    // Mode C: Full chain context
    const historyText = input.chainHistory
      .map(h => `Position ${h.position} Output:\n${typeof h.output === 'string' ? h.output : JSON.stringify(h.output, null, 2)}`)
      .join('\n\n');
    
    formattedPrompt = `${promptTemplate}

---
ORIGINAL USER INPUT:
${typeof input.originalInput === 'string' ? input.originalInput : JSON.stringify(input.originalInput, null, 2)}

CHAIN HISTORY:
${historyText}
---

Based on the full context above, provide your refined output:`;
  }
  
  return formattedPrompt;
};

class ChainExecutionService {
  constructor() {
    this.queryRegistry = queryRegistryService;
  }

  /**
   * Resolve the chain for a given query
   * @param {string} baseQueryId - The base query ID (e.g., 'chartmind-ddx')
   * @param {Object} userContext - User context including region, admin, etc.
   * @returns {Promise<Array>} Array of queries in chain order [position1, position2, position3]
   */
  async resolveChain(baseQueryId, userContext = {}) {
    const { region, activeChartmindAdmin, activeClinicId } = userContext;
    
    const chain = [];
    
    // Position 1: Global prompt (always exists)
    // Try base query first (most common), then -global suffix variant
    const position1 = await this.queryRegistry.getQuery(baseQueryId) ||
                      await this.queryRegistry.getQuery(`${baseQueryId}-global`);
    if (position1) {
      chain.push({
        position: CHAIN_POSITIONS.GLOBAL,
        query: position1,
        inputMode: INPUT_MODES.PREVIOUS_ONLY, // Position 1 uses original input
      });
    }
    
    // Position 2: Regional prompt (if exists and user has region)
    if (region) {
      const position2Id = `${baseQueryId}-regional-${region}`;
      const position2 = await this.queryRegistry.getQuery(position2Id);
      if (position2) {
        chain.push({
          position: CHAIN_POSITIONS.REGIONAL,
          query: position2,
          inputMode: position2.chainConfig?.inputMode || INPUT_MODES.PREVIOUS_PLUS_ORIGINAL,
        });
      }
    }
    
    // Position 3: Clinic-scoped (when provider in clinic context) or ChartMind Admin / Template
    // Clinic-only for ChartMind: when activeClinicId is set, use clinic P3 only (no designee fallback)
    const isChartMindQuery = baseQueryId && String(baseQueryId).startsWith('chartmind-');
    if (activeClinicId && isChartMindQuery) {
      const clinicP3 = await this.queryRegistry.getClinicPosition3(activeClinicId, baseQueryId);
      if (clinicP3?.prompt) {
        chain.push({
          position: CHAIN_POSITIONS.LOCAL,
          query: {
            id: `${baseQueryId}-local-clinic-${activeClinicId}`,
            prompt: clinicP3.prompt,
            model: clinicP3.model || DEFAULT_MODEL,
            temperature: clinicP3.temperature !== undefined ? clinicP3.temperature : 0.7,
            maxTokens: clinicP3.maxTokens !== undefined ? clinicP3.maxTokens : 2000,
            source: 'clinic',
          },
          inputMode: clinicP3.inputMode || INPUT_MODES.PREVIOUS_PLUS_ORIGINAL,
        });
      }
    } else if (activeChartmindAdmin?.templateId) {
      // Template-based P3: fetch prompts from the templates subcollection
      const cardId = this.extractCardId(baseQueryId);
      const templateP3 = await this.queryRegistry.getTemplatePosition3(
        activeChartmindAdmin.id,
        activeChartmindAdmin.templateId,
        cardId
      );
      
      if (templateP3?.enabled && templateP3?.prompt) {
        chain.push({
          position: CHAIN_POSITIONS.LOCAL,
          query: {
            id: `${baseQueryId}-local-template-${activeChartmindAdmin.templateId}`,
            prompt: templateP3.prompt,
            model: templateP3.model || DEFAULT_MODEL,
            temperature: templateP3.temperature || 0.7,
            maxTokens: templateP3.maxTokens || 2000,
            source: 'chartmind-template',
            adminId: activeChartmindAdmin.id,
            templateId: activeChartmindAdmin.templateId,
          },
          inputMode: templateP3.inputMode || INPUT_MODES.PREVIOUS_PLUS_ORIGINAL,
        });
      }
    } else if (activeChartmindAdmin?.customPrompts) {
      // Legacy: admin-level customPrompts (no template)
      const cardId = this.extractCardId(baseQueryId);
      const customPrompt = activeChartmindAdmin.customPrompts[cardId];
      
      if (customPrompt?.enabled && customPrompt?.prompt) {
        chain.push({
          position: CHAIN_POSITIONS.LOCAL,
          query: {
            id: `${baseQueryId}-local-${activeChartmindAdmin.id}`,
            prompt: customPrompt.prompt,
            model: customPrompt.model || DEFAULT_MODEL,
            temperature: customPrompt.temperature || 0.7,
            maxTokens: customPrompt.maxTokens || 2000,
            source: 'chartmind-admin',
            adminId: activeChartmindAdmin.id,
          },
          inputMode: customPrompt.inputMode || INPUT_MODES.PREVIOUS_PLUS_ORIGINAL,
        });
      }
    }
    
    return chain;
  }

  /**
   * Execute a chain of prompts
   * @param {string} baseQueryId - The base query ID
   * @param {string|Object} originalInput - The original user input
   * @param {Object} userContext - User context
   * @param {Function} llmCallFn - Function to call the LLM (prompt, config) => Promise<response>
   * @returns {Promise<Object>} The final output and chain execution details
   */
  async executeChain(baseQueryId, originalInput, userContext, llmCallFn) {
    // Resolve the chain
    const chain = await this.resolveChain(baseQueryId, userContext);
    
    if (chain.length === 0) {
      const errorMsg = `No prompts found for query: ${baseQueryId}. Please ensure the query exists in the LLM Query Manager or run the migration script.`;
      console.error(`[ChainExecution] ${errorMsg}`);
      throw new Error(errorMsg);
    }
    
    // Execute the chain
    const chainHistory = [];
    let previousOutput = null;
    let finalOutput = null;
    
    for (const chainLink of chain) {
      const { position, query, inputMode } = chainLink;
      
      // Build input for this position
      let positionInput;
      if (position === CHAIN_POSITIONS.GLOBAL) {
        // Position 1 always uses original input
        positionInput = originalInput;
      } else {
        // Position 2 and 3 use the configured input mode
        const inputContext = {
          originalInput,
          previousOutput,
          chainHistory: [...chainHistory],
        };
        positionInput = buildPositionInput(inputMode, inputContext);
      }
      
      // Format the prompt
      let formattedPrompt = position === CHAIN_POSITIONS.GLOBAL
        ? query.prompt
        : formatPromptWithInput(query.prompt, positionInput, inputMode);
      
      // Validate prompt for unsafe patterns
      // Skip validation for hardcoded prompts (they're trusted) - check if query has uiLocation (hardcoded indicator)
      const isHardcodedPrompt = query.uiLocation || query.source === 'hardcoded';
      
      if (!isHardcodedPrompt) {
        const promptValidation = validatePrompt(formattedPrompt);
        if (!promptValidation.isValid) {
          console.error(`[ChainExecution] Unsafe prompt detected at position ${position}:`, promptValidation.issues);
          console.error(`[ChainExecution] Query ID: ${query.id}, Issues:`, JSON.stringify(promptValidation.issues, null, 2));
          throw new Error('Prompt validation failed: Unsafe patterns detected. Please review and correct the prompt.');
        }
        
        if (promptValidation.hasWarnings) {
          console.warn(`[ChainExecution] Prompt warnings at position ${position}:`, promptValidation.warnings);
        }
      } else {
      }
      
      // Inject document context (vector search) ONLY if RAG is explicitly enabled
      // Get document settings from query if available
      const documentSettings = query.documentSettings || {};
      const { limitToVectorStore = false, supplementAuthoritativeResources = false } = documentSettings;
      
      // Only run RAG if one of the toggles is enabled
      if (limitToVectorStore || supplementAuthoritativeResources) {
        try {
          const queryText = typeof originalInput === 'string' 
            ? originalInput 
            : JSON.stringify(originalInput);
          
          formattedPrompt = await vectorSearchService.injectDocumentContext(
            formattedPrompt,
            baseQueryId,
            position,
            queryText,
            {
              adminId: userContext.activeChartmindAdmin?.id || null,
              topK: 10, // Generous number of chunks
              format: 'text', // Formatted text (not JSON)
              minConfidence: 0.7, // Confidence threshold
              userId: userContext.userId,
              documentSettings, // Pass document settings (limitToVectorStore, supplementAuthoritativeResources)
              useReranking: true, // Enable reranking
              useMMR: true, // Enable MMR for diversity
              useHybridSearch: false, // Can enable if needed
              embeddingModel: 'text-embedding-3-large', // Use large model
              maxTokens: 4000, // Context window limit
              reservedTokens: 500, // Reserve for prompt
            }
          );
        } catch (error) {
          // Don't fail if vector search fails - just log and continue
          console.warn(`[ChainExecution] Vector search failed for position ${position}:`, error);
        }
      } else {
      }
      
      // Add medical disclaimer for medical queries
      if (query.category === QUERY_CATEGORIES.CHARTMIND || 
          query.category === QUERY_CATEGORIES.SYMPTOM_CHECKER) {
        formattedPrompt = addMedicalDisclaimer(formattedPrompt);
      }
      
      // Call the LLM
      try {
        // If llmCallFn is provided, use it (for backward compatibility)
        // Otherwise, use the unified llmProviderService
        let response;
        if (llmCallFn) {
          // Pass positionInput as third argument so llmCallFn can include it as user message
          response = await llmCallFn(formattedPrompt, {
            model: query.model,
            temperature: query.temperature,
            maxTokens: query.maxTokens,
          }, positionInput);
        } else {
          // Use unified LLM provider service
          const { detectProviderFromModel } = await import('./llmProviderService');
          const detectedProvider = detectProviderFromModel(query.model);
          
          // Build user message content from positionInput
          const userMessageContent = typeof positionInput === 'string' 
            ? positionInput 
            : (positionInput?.input || positionInput?.originalInput || JSON.stringify(positionInput));
          
          response = await llmProviderService.callLLM({
            model: query.model,
            provider: detectedProvider,
            messages: [
              { role: 'system', content: formattedPrompt },
              { role: 'user', content: userMessageContent },
            ],
            temperature: query.temperature,
            maxTokens: query.maxTokens,
            module: baseQueryId,
            featureName: `position_${position}`,
          });
          // Extract content from response (may be wrapped)
          response = response?.content || response?.text || response;
        }
        
        // Store the output
        previousOutput = response;
        finalOutput = response;
        
        chainHistory.push({
          position,
          queryId: query.id,
          output: response,
          inputMode,
          source: query.source || 'unknown', // Track prompt source (firestore/hardcoded)
        });
        
      } catch (error) {
        console.error(`[ChainExecution] Error at position ${position}:`, error);
        
        // If a position fails, use the previous output as final
        // Don't throw - allow partial chain execution
        break;
      }
    }
    
    return {
      output: finalOutput,
      chainHistory,
      chainLength: chain.length,
      executedPositions: chainHistory.length,
      isComplete: chainHistory.length === chain.length,
      promptSource: chain[0]?.query?.source || 'unknown', // Primary source for Position 1
    };
  }

  /**
   * Execute a single position in a chain (for testing/preview)
   * @param {Object} query - The query configuration
   * @param {Object} input - The input context
   * @param {string} inputMode - The input mode
   * @param {Function} llmCallFn - Function to call the LLM
   * @returns {Promise<Object>} The position output
   */
  async executePosition(query, input, inputMode, llmCallFn) {
    const positionInput = buildPositionInput(inputMode, input);
    const formattedPrompt = formatPromptWithInput(query.prompt, positionInput, inputMode);
    
    const response = await llmCallFn(formattedPrompt, {
      model: query.model,
      temperature: query.temperature,
      maxTokens: query.maxTokens,
    });
    
    return {
      output: response,
      formattedPrompt, // For debugging/preview
      inputMode,
    };
  }

  /**
   * Preview the chain without executing (for debugging)
   * @param {string} baseQueryId - The base query ID
   * @param {Object} userContext - User context
   * @returns {Promise<Object>} Chain preview details
   */
  async previewChain(baseQueryId, userContext) {
    const chain = await this.resolveChain(baseQueryId, userContext);
    
    return {
      queryId: baseQueryId,
      chainLength: chain.length,
      positions: chain.map(link => ({
        position: link.position,
        queryId: link.query.id,
        source: link.query.source,
        inputMode: link.inputMode,
        hasPrompt: !!link.query.prompt,
        model: link.query.model,
      })),
    };
  }

  /**
   * Extract card ID from query ID
   * @param {string} queryId - The query ID (e.g., 'chartmind-ddx-global')
   * @returns {string} The card ID (e.g., 'ddx')
   */
  extractCardId(queryId) {
    // Query IDs follow pattern: {feature}-{cardId} or {feature}-{cardId}-{scope}
    const parts = queryId.split('-');
    
    // Common patterns:
    // 'chartmind-ddx' → 'ddx'
    // 'chartmind-ddx-global' → 'ddx'
    // 'chartmind-tests-regional-east_africa' → 'tests'
    
    if (parts[0] === 'chartmind' && parts.length >= 2) {
      return parts[1];
    }
    
    return queryId;
  }

  /**
   * Get the default input mode description
   * @param {string} mode - The input mode
   * @returns {string} Human-readable description
   */
  getInputModeDescription(mode) {
    switch (mode) {
      case INPUT_MODES.PREVIOUS_ONLY:
        return 'Previous output only';
      case INPUT_MODES.PREVIOUS_PLUS_ORIGINAL:
        return 'Previous output + original input';
      case INPUT_MODES.FULL_CONTEXT:
        return 'Full chain context';
      default:
        return 'Unknown';
    }
  }
}

// Export singleton instance
export const chainExecutionService = new ChainExecutionService();

export default chainExecutionService;
